import React from "react";
import { useClient } from "utils/client";
import { useHistory } from "react-router";
import { useTranslation } from "react-i18next";
import { AxiosError, AxiosInstance } from "axios";
import { useMutation, useQueryClient } from "react-query";

import { Risk, RiskMitigation } from "../types";
import { Project } from "types";
import { FormCb } from "../formdata";
import { ControlContext } from "context/Controls";
import { TrackingContext } from "context/Tracking";
import { UploadType } from "components/Documents/types";
import { FeedbackContext } from "context/Feedback";
import { FeedbackLocation } from "components/Feedback/FeedbackPopup";
import { useProjectDetails } from "pages/Details/hooks/useProjectDetails";
import {
  requestDelete,
  uploadSignedS3,
} from "pages/Project/hooks/useCreateProject";

const updateRisk = async (
  client: AxiosInstance,
  { risk, mitigations, _uploads }: FormCb,
  riskId: string,
  project: Project,
  projectId: string
) => {
  try {
    // @ts-ignore
    project.risks = project.risks.map((oldRisk: Risk) => {
      if (oldRisk.resource_id !== riskId) {
        return oldRisk;
      }

      return {
        ...risk,
        resource_id: riskId,
        mitigations: mitigations.map((mitigation) => ({
          ...mitigation,
          impact: mitigation.impact || 0,
          people_count: mitigation.people_count || 0,
          duration: mitigation.duration || 0,
          probability: mitigation.probability || 0,
          detectability: mitigation.detectability || 0,
          applied_standards: mitigation.applied_standards || [],
        })),
        assessment: {
          impact: risk.impact || 0,
          people_count: risk.people_count || 0,
          duration: risk.duration || 0,
          probability: risk.probability || 0,
          detectability: risk.detectability || 0,
        },
        impact: risk.impact || 0,
        people_count: risk.people_count || 0,
        duration: risk.duration || 0,
        probability: risk.probability || 0,
        detectability: risk.detectability || 0,
      };
    });

    await client.put(`/projects/${projectId}`, {
      payload: project,
    });

    // Make sure to upload after the project was updated
    for (const fileKey of Object.keys(_uploads)) {
      const file = _uploads[fileKey];

      if (!file || !file.touched) continue;

      if (file.touched) {
        await requestDelete(
          client,
          `${fileKey}::/upload/risks/${riskId}/`,
          projectId
        );
      }

      if (!file.image && !file.key) continue;

      const uploadName = `/upload/risks/${riskId}/${file.image.name}`;
      const prefix = `${projectId}${uploadName}`;

      await uploadSignedS3(client, prefix, _uploads[fileKey].image);

      await client.post(`/projects/${projectId}/documents`, {
        type: fileKey as UploadType,
        name: uploadName,
      });
    }
  } catch (error) {
    console.error(error);
    throw new Error((error as AxiosError).response?.data);
  }

  return risk;
};

export const useUpdateRisk = (
  projectId: string,
  risk: Risk,
  project?: Project
) => {
  const client = useClient();
  const history = useHistory();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { track } = React.useContext(TrackingContext);
  const { displayFeedback } = React.useContext(FeedbackContext);
  const { displayNotification } = React.useContext(ControlContext);

  const { mutate, mutateAsync } = useMutation(
    async (v: FormCb & { isAutoSave?: boolean }) =>
      await updateRisk(client, v, risk.resource_id, project!, projectId),
    {
      onError: (e) => {
        // TODO: we can actually parse out the exact field that couldn't be parsed
        // from the error message and then display a more specific error message
        const isParsingError = (e as Error).message.match(
          "cannot unmarshal string into Go struct"
        );

        if (isParsingError) {
          displayNotification!(
            t("risks.save_notification.error"),
            t("risks.save_notification.parsing_error"),
            "error"
          );

          return;
        }
        displayNotification!(
          "Error creating Risk",
          (e as Error).message,
          "error"
        );
      },
      onSuccess: (newRisk, { isAutoSave }) => {
        if (isAutoSave) {
          displayNotification!(
            t("notification.saving.autosave_success"),
            "",
            "success"
          );

          return;
        }
        queryClient.refetchQueries(["risks", projectId]);
        queryClient.refetchQueries(["projects", projectId]);

        history.push(`/projects/${projectId}/risks?saved=true`);

        track("update risk");
        displayFeedback!(FeedbackLocation.Risk);
        displayNotification!(t("notification.saving.generic_success"), "");
      },
    }
  );

  return { mutate, mutateAsync };
};

const deleteRisk = async (
  client: AxiosInstance,
  projectId: string,
  riskId: string,
  project: Project
) => {
  project.riskGrouping = project.riskGrouping?.map((group) => {
    group.members = group.members?.filter((id) => id !== riskId);
    return group;
  });

  project.risks = project.risks.filter(
    ({ resource_id }: any) => resource_id !== riskId
  );

  await client.put(`/projects/${projectId}`, { payload: project });
};

export const useDeleteRisk = (projectId: string, riskId: string) => {
  const client = useClient();
  const history = useHistory();
  const queryClient = useQueryClient();
  const { project } = useProjectDetails(projectId);
  const { track } = React.useContext(TrackingContext);
  const { displayFeedback } = React.useContext(FeedbackContext);
  const { displayNotification } = React.useContext(ControlContext);

  const { mutate } = useMutation(
    async () => await deleteRisk(client, projectId, riskId, project!),
    {
      onError: (e) =>
        displayNotification!(
          "Error deleting hazard",
          (e as Error).message,
          "error"
        ),
      onSuccess: () => {
        queryClient.refetchQueries(["risks", projectId]);
        queryClient.refetchQueries(["projects", projectId]);

        history.push(`/projects/${projectId}/risks?saved=true`);

        track("delete risk");
        displayFeedback!(FeedbackLocation.Risk);
        displayNotification!("Gefährdung gelöscht!", "");
      },
    }
  );

  return mutate;
};
